import { differenceInDays } from 'date-fns';
import {
  assoc,
  assocPath,
  dissoc,
  find,
  includes,
  is,
  isEmpty,
  map,
  merge,
  mergeDeepRight,
  not,
  omit,
  pathOr,
  pick,
  prop,
  propEq,
  sortBy,
  isNil,
  filter,
  last,
  allPass,
} from 'ramda';
import coreApi from '../coreApi';
import {
  FNOL_CUSTOMER_ANSWER,
  MISSION_TYPE,
  VEHICLE_DESINATION_REASON,
  VEHICLE_DESINATION_TYPE,
  ERROR_CODES,
  MISSION_STATUS,
  CAPTCHA_ACTIONS,
  PUBLIC_API_VERSION,
  HISTORY_EVENTS,
  BENEFITS,
} from '../enums';
import { getAppointment } from '../selectors/appointment';
import { getCarDestination } from '../selectors/carDestination';
import {
  getAccessToken,
  getAuth,
  getClaim,
  getClaimAddress,
  getClaimHighwayMetres,
  getClaimId,
  getClaimPosition,
  getMission,
  getMissionId,
  getPreviousClaimId,
  getPreviousMissionId,
  getRefreshToken,
  getScopeStatus,
  getCarDestinations as getRecommendedCarDestinations,
  getJwt,
  getAllowHidingDestinationProcessInDigital,
  getCoreRentalMissionId,
  getCoreRentalMission,
  getEventCode,
  getBenefit,
  getBusinessPartnerCode,
} from '../selectors/coreApi';
import { getEvent, getEventDetails } from '../selectors/diagnosis';
import {
  getBrand,
  getCountry,
  getFirstName,
  getLastName,
  getModel,
  getEnergy,
  getEnergyType,
  getMileage,
  getOtp,
  getPhoneNumber,
  getPlateNumber,
  getVinNumber,
  getRegistrationDate,
  getType,
  getWeight,
  getLeaserContractNumber,
} from '../selectors/eligibility';
import { getErrorCode } from '../selectors/error';
import { getFnol } from '../selectors/fnol';
import { getSession, getNpsAnswer } from '../selectors/global';
import { getHighwayMetres, getLocation, getPosition, getCountry as getLocationCountry } from '../selectors/location';
import { getEventSource } from '../selectors/missionMonitoring';
import { getIsPublicApiV1, getIsPublicApiV4, getIsPublicApiV5, getSettings } from '../selectors/settings';
import {
  getRentalDrivers,
  getRentalVehicleDetails,
  getRentalBookingDetails,
  getRentalVehicleOtherGarageName,
  getRentalVehicleOtherGarageEmail,
  getRentalVehicleOtherGaragePhone,
  getRentalVehicleOtherLocationAddressObject,
  getRentalExtensionDurationInMinutes,
} from '../selectors/rentalMission';
import {
  extractMissionHistory,
  extractUniqueClaimHistory,
  isEligible,
  isMissionMonitoringIsEnded,
  isROSMission,
  isRentalMissionOngoing,
  isRentalService,
  isTOWMission,
  lastMissionTowOrRos,
} from '../utils/coreApi';
import * as webStorage from '../webStorage';
import { requestCaptchaToken } from './recaptcha';
import { eventTags } from '../services/analytics/tags';
import piwik from '../services/analytics';

export const RESET_RETRY_COUNTER = 'DRSA/COREAPI/RESET_REQUEST_COUNTER';
export const RETRY_NOT_FOUND_MODAL = 'DRSA/COREAPI/RETRY_NOT_FOUND_MODAL';
export const RETRY_EXPIRED_MODAL = 'DRSA/COREAPI/RETRY_EXPIRED_MODAL';
export const RETRY_CLAIM_EXIST = 'DRSA/COREAPI/RETRY_CLAIM_EXIST';
export const START_REQUEST = 'DRSA/COREAPI/START_REQUEST';
export const SESSION_ERROR = 'DRSA/COREAPI/SESSION_ERROR';
export const REQUEST_ERROR = 'DRSA/COREAPI/REQUEST_ERROR';
export const REQUEST_SUCCESS = 'DRSA/COREAPI/REQUEST_SUCCESS';
export const AUTH_SUCCESS = 'DRSA/COREAPI/AUTH_SUCCESS';
export const AUTH_ERROR = 'DRSA/COREAPI/AUTH_ERROR';
export const SCOPE_ERROR = 'DRSA/COREAPI/SCOPE_ERROR';
export const AUTHENTICATED = 'DRSA/COREAPI/AUTHENTICATED';
export const IDENTIFIED = 'DRSA/COREAPI/IDENTIFIED';
export const APPROVED = 'DRSA/COREAPI/APPROVED';
export const CLAIM_CREATED = 'DRSA/COREAPI/CLAIM_CREATED';
export const PATCH_CLAIM = 'DRSA/COREAPI/PATCH_CLAIM';
export const CLAIM_PATCHED = 'DRSA/COREAPI/CLAIM_PATCHED';
export const CLAIM_LOADED = 'DRSA/COREAPI/CLAIM_LOADED';
export const RENTAL_CLAIM_LOADED = 'DRSA/COREAPI/RENTAL_CLAIM_LOADED';
export const NO_CLAIM_FOUND = 'DRSA/COREAPI/NO_CLAIM_FOUND';
export const CLAIM_ERROR = 'DRSA/COREAPI/CLAIM_ERROR';
export const MISSION_LOADED = 'DRSA/COREAPI/MISSION_LOADED';
export const MISSIONS_LOADED = 'DRSA/COREAPI/MISSIONS_LOADED';
export const NO_MISSION_FOUND = 'DRSA/COREAPI/NO_MISSION_FOUND';
export const CREATE_MISSION = 'DRSA/COREAPI/CREATE_MISSION';
export const MISSION_CREATED = 'DRSA/COREAPI/MISSION_CREATED';
export const CREATE_RENTAL_MISSION = 'DRSA/COREAPI/CREATE_RENTAL_MISSION';
export const RENTAL_MISSION_CREATED = 'DRSA/COREAPI/RENTAL_MISSION_CREATED';
export const RENTAL_MISSION_UPDATED = 'DRSA/COREAPI/RENTAL_MISSION_UPDATED';
export const RENTAL_MISSION_CANCELED = 'DRSA/COREAPI/RENTAL_MISSION_CANCELED';
export const RENTAL_EXTENSION_CANCELED = 'DRSA/COREAPI/RENTAL_EXTENSION_CANCELED';
export const RENTAL_EXTENSION_SENT = 'DRSA/COREAPI/RENTAL_EXTENSION_SENT';
export const MISSION_DISPATCHED = 'DRSA/COREAPI/MISSION_DISPATCHED';
export const RENTAL_MISSION_DISPATCHED = 'DRSA/COREAPI/RENTAL_MISSION_DISPATCHED';
export const MISSION_PATCHED = 'DRSA/COREAPI/MISSION_PATCHED';
export const MISSION_CONFIRMED = 'DRSA/COREAPI/MISSION_CONFIRMED';
export const MISSION_STARTED = 'DRSA/COREAPI/MISSION_STARTED';
export const CANCEL_MISSION = 'DRSA/COREAPI/CANCEL_MISSION';
export const MISSION_CANCELED = 'DRSA/COREAPI/MISSION_CANCELED';
export const REQUEST_CAR_DESTINATIONS = 'DRSA/COREAPI/REQUEST_CAR_DESTINATIONS';
export const REQUEST_SPECIFIC_CAR_DESTINATIONS = 'REQUEST_SPECIFIC_CAR_DESTINATIONS';
export const REQUEST_CAR_DESTINATIONS_RULES = 'DRSA/COREAPI/REQUEST_CAR_DESTINATIONS_RULES';
export const CAR_DESTINATIONS = 'DRSA/COREAPI/CAR_DESTINATIONS';
export const SPECIFIC_CAR_DESTINATIONS = 'DRSA/COREAPI/SPECIFIC_CAR_DESTINATION';
export const CAR_DESTINATIONS_RULES = 'DRSA/COREAPI/CAR_DESTINATIONS_RULES';
export const CAR_DESTINATIONS_INFO_LOADED = 'DRSA/COREAPI/CAR_DESTINATIONS_INFO_LOADED';
export const DEC_REQUEST_COUNTER = 'DRSA/COREAPI/DEC_REQUEST_COUNTER';
export const INC_REQUEST_COUNTER = 'DRSA/COREAPI/INC_REQUEST_COUNTER';
export const MISSION_MONITORING_STARTED = 'DRSA/COREAPI/MISSION_MONITORING_STARTED';
export const MISSION_MONITORING_ENDED = 'DRSA/COREAPI/MISSION_MONITORING_ENDED';
export const MISSION_MONITORING_ENDING = 'DRSA/COREAPI/MISSION_MONITORING_ENDING';
export const MISSION_MONITORING_EVENT = 'DRSA/COREAPI/MISSION_MONITORING_EVENT';
export const TASK_CREATED = 'DRSA/COREAPI/TASK_CREATED';
export const PERSON_SPECIAL_NEEDS_ERROR = 'DRSA/COREAPI/PERSON_SPECIAL_NEEDS_ERROR';
export const NO_CAR_DESTINATION = 'DRSA/COREAPI/NO_CAR_DESTINATION';
export const DIGITAL_INTERACTIONS = 'DRSA/COREAPI/DIGITAL_INTERACTIONS';
export const VOICE_EXIT_ERROR = 'DRSA/COREAPI/VOICE_EXIT_ERROR';
export const CREATE_MISSION_ERROR = 'DRSA/COREAPI/CREATE_MISSION_ERROR';
export const EVENTS_DETAILS_ERROR = 'DRSA/COREAPI/EVENTS_DETAILS_ERROR';
export const VEHICLE_LOCATION_ERROR = 'DRSA/COREAPI/VEHICLE_LOCATION_ERROR';
export const CAR_DESTINATION_ERROR = 'DRSA/COREAPI/CAR_DESTINATION_ERROR';
export const CONFIRMATION_ERROR = 'DRSA/COREAPI/CONFIRMATION_ERROR';
export const MISSION_MONITORING_ERROR = 'DRSA/COREAPI/MISSION_MONITORING_ERROR';
export const PROVIDER_ARRIVAL_ERROR = 'DRSA/COREAPI/PROVIDER_ARRIVAL_ERROR';
export const CLEAR_MISSION_MONITORING = 'DRSA/COREAPI/CLEAR_MISSION_MONITORING';
export const LOCATION_DETAILS_ERROR = 'DRSA/COREAPI/LOCATION_DETAILS_ERROR';
export const LOCATION_DETAILS = 'DRSA/COREAPI/LOCATION_DETAILS';
export const CANCEL_APPOINTMENT = 'DRSA/COREAPI/CANCEL_APPOINTMENT';
export const UPDATE_APPOINTMENT = 'DRSA/COREAPI/UPDATE_APPOINTMENT';
export const CONTINUE_PROCESS = 'DRSA/COREAPI/CONTINUE_PROCESS';
export const CANCEL_MILESTONE = 'DRSA/COREAPI/CANCEL_MILESTONE';
export const GET_HIGHWAY_SECTIONS = 'DRSA/COREAPI/GET_HIGHWAY_SECTIONS';
export const HIGHWAY_SECTIONS_FOUND = 'DRSA/COREAPI/HIGHWAY_SECTIONS_FOUND';
export const CANCEL_HIGHWAY_DIRECTION = 'DRSA/COREAPI/CANCEL_HIGHWAY_DIRECTION';
export const HIGHWAY_SECTIONS_NOT_FOUND = 'DRSA/COREAPI/HIGHWAY_SECTIONS_NOT_FOUND';
export const ANOTHER_REQUEST = 'DRSA/COREAPI/ANOTHER_REQUEST';
export const RENTAL_BENEFIT_COVERAGE_ISSUE = 'DRSA/COREAPI/RENTAL_BENEFIT_COVERAGE_ISSUE';
export const RENTAL_MISSION_ALREADY_EXISTS = 'DRSA/COREAPI/RENTAL_MISSION_ALREADY_EXISTS';
export const CANCEL_PREVIOUS_CLAIM_HANDLING = 'DRSA/COREAPI/CANCEL_PREVIOUS_CLAIM_HANDLING';
export const VEHICLE_INFO_LOADED = 'DRSA/COREAPI/VEHICLE_INFO_LOADED';
export const CLAIM_VEHICLE_LOSS_PUTTED = 'DRSA/COREAPI/CLAIM_VEHICLE_LOSS_PUTTED';
export const PUT_CLAIM = 'DRSA/COREAPI/PUT_CLAIM';
export const CLAIM_PUTTED = 'DRSA/COREAPI/CLAIM_PUTTED';
export const MISSION_PUTTED = 'DRSA/COREAPI/MISSION_PUTTED';
export const CLEAR_VEHICLE_DESTINATION_VALUES = 'DRSA/COREAPI/CLEAR_VEHICLE_DESTINATION_VALUES';
export const CLAIM_DIGITAL_EXIT_CREATED = 'DRSA/COREAPI/CLAIM_DIGITAL_EXIT_CREATED';
export const OTP_REQUESTED = 'DRSA/COREAPI/OTP_REQUESTED';
export const JWT_CREATED = 'DRSA/COREAPI/JWT_CREATED';
export const NPS_ELIGIBILITY_CLAIM = 'DRSA/COREAPI/NPS_ELIGIBILITY_CLAIM';
export const PUT_NPS_ELIGIBILITY_CLAIM = 'DRSA/COREAPI/PUT_NPS_ELIGIBILITY_CLAIM';
export const CLEAR_JWT = 'DRSA/COREAPI/CLEAR_JWT';
export const PRODUCT_CONFIGURATION_LOADED = 'DRSA/COREAPI/PRODUCT_CONFIGURATION_LOADED';
export const CLAIM_HISTORY_DISPATCH = 'DRSA/COREAPI/CLAIM_HISTORY_DISPATCH';
export const CSC_ELIGIBILE = 'DRSA/COREAPI/CSC_ELIGIBILE';
export const IMMOBILISATION_GARAGE_UPDATED = 'DRSA/COREAPI/IMMOBILISATION_GARAGE_UPDATED';
export const VIEW_RENTAL_SUMMARY = 'DRSA/COREAPI/VIEW_RENTAL_SUMMARY';
export const VIEW_TIMELINE = 'DRSA/COREAPI/VIEW_TIMELINE';
export const TIMEOUT = 'DRSA/COREAPI/TIMEOUT';
export const SELECT_BENEFIT = 'DRSA/COREAPI/SELECT_BENEFIT';
export const RENTAL_EXTENSION_WORKFLOW = 'DRSA/CORE_API/RENTAL_EXTENSION_WORKFLOW';
export const PHONE_CONTACT = 'DRSA/CORE_API/PHONE_CONTACT';
export const RENTAL_CURRENT_LIMITS = 'DRSA/CORE_API/RENTAL_CURRENT_LIMITS';

export const errors = {
  [SCOPE_ERROR]: 'SCOPE_ERROR',
  [AUTH_ERROR]: 'AUTH_ERROR',
  [CLAIM_ERROR]: 'CLAIM_ERROR',
  [PERSON_SPECIAL_NEEDS_ERROR]: 'PERSON_SPECIAL_NEEDS_ERROR',
  [NO_CAR_DESTINATION]: 'NO_CAR_DESTINATION',
  [CREATE_MISSION_ERROR]: 'CREATE_MISSION_ERROR',
  [EVENTS_DETAILS_ERROR]: 'EVENTS_DETAILS_ERROR',
  [VEHICLE_LOCATION_ERROR]: 'VEHICLE_LOCATION_ERROR',
  [CAR_DESTINATION_ERROR]: 'CAR_DESTINATION_ERROR',
  [CONFIRMATION_ERROR]: 'CONFIRMATION_ERROR',
  [MISSION_MONITORING_ERROR]: 'MISSION_MONITORING_ERROR',
  [PROVIDER_ARRIVAL_ERROR]: 'PROVIDER_ARRIVAL_ERROR',
  [LOCATION_DETAILS_ERROR]: 'LOCATION_DETAILS_ERROR',
};

export const CAR_DESTINATION_NOT_FOUND = 404;

export const digitalExitReasonsToBeCalledBack = [
  ERROR_CODES.CLAIM_EVENT_MISSING,
  ERROR_CODES.CLAIM_LOCATION_MISSING,
  ERROR_CODES.CLAIM_EVENT_DETAILS_MISSING,
  ERROR_CODES.CLAIM_LOCATION_DETAILS_MISSING,
  ERROR_CODES.MISSION_ALREADY_EXISTS,
  ERROR_CODES.CLAIM_ANTERIORITY_ISSUE,
  ERROR_CODES.CLAIM_COVERAGE_ISSUE,
  ERROR_CODES.HOME_DISTANCE_ISSUE,
  ERROR_CODES.CLAIM_WARRANTY_ISSUE,
  ERROR_CODES.CRANE_REQUIRED,
  ERROR_CODES.VEHICLE_IS_ON_A_HIGHWAY,
  ERROR_CODES.NO_SUITABLE_SERVICE_FOUND,
  ERROR_CODES.MISSION_OVERCOST,
  ERROR_CODES.SERVICE_DOESNT_ALLOW_DISPATCH,
  ERROR_CODES.HIGHWAY_SECTIONS_NON_AVAILABLE,
  ERROR_CODES.CANCEL_PREVIOUS_CLAIM_HANDLING,
  ERROR_CODES.CANCEL_MILESTONE,
  ERROR_CODES.CANCEL_HIGHWAY_DIRECTION,
  ERROR_CODES.CANCEL_APPOINTMENT,
  ERROR_CODES.UPDATE_APPOINTMENT,
  ERROR_CODES.PROVIDER_NOT_ON_SPOT,
  ERROR_CODES.ADDITIONAL_SERVICE,
  ERROR_CODES.UNKNOWN_DESTINATION,
  ERROR_CODES.INCORRECT_VEHICLE_DESTINATION,
  ERROR_CODES.ADDITIONAL_TRANSPORTATION_NEEDED,
  ERROR_CODES.BENEFIT_COVERAGE_ISSUE,
  ERROR_CODES.EVENT_TO_POLICY_ADDRESS_EXCEEDED,
  ERROR_CODES.OVER_DISTANCE_LIMIT_EXCEEDED,
  ERROR_CODES.VEHICLE_IS_ON_AN_UNAUTHORIZED_HIGHWAY,
  ERROR_CODES.FRAUD_ALERT,
  ERROR_CODES.TECHNICAL_ERROR,
  ERROR_CODES.RENTAL_BENEFIT_COVERAGE_ISSUE,
  ERROR_CODES.BO_CSC_BENEFIT_COVERAGE_ISSUE,
  ERROR_CODES.RENTAL_CAR_BENEFITS_NOT_COVERED,
];

const initialState = { hasTechnicalError: false, retryCounter: {}, pendingRequests: 0 };

const reducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case TASK_CREATED:
      return {
        ...state,
        task: action.task,
      };
    case MISSION_MONITORING_EVENT:
      return {
        ...state,
        mission: action?.payload?.mainMission
          ? mergeDeepRight(state.mission, action?.payload?.mainMission)
          : state.mission,
        missionMonitoring: {
          ...(action?.payload?.mainMission
            ? mergeDeepRight(state.missionMonitoring, action?.payload?.mainMission)
            : state.missionMonitoring),
          id: action.id,
          timestamp: action.timestamp,
        },
        rentalMission: action?.payload?.rentalMission
          ? mergeDeepRight(state.rentalMission, action?.payload?.rentalMission)
          : state.rentalMission,
      };
    case MISSION_MONITORING_STARTED:
      return {
        ...state,
        missionMonitoring: {
          ...state.missionMonitoring,
          eventSource: action.eventSource,
        },
      };
    case CLEAR_MISSION_MONITORING:
      return {
        ...state,
        missionMonitoring: {
          eventSource: state.missionMonitoring.eventSource,
        },
      };
    case CAR_DESTINATIONS_RULES:
      return {
        ...state,
        carDestinationsRules: action.rules,
      };
    case CAR_DESTINATIONS:
      return {
        ...state,
        carDestinations: action.destinations,
      };
    case REQUEST_SPECIFIC_CAR_DESTINATIONS:
      return {
        ...state,
        specificDestinationsLoaded: false,
      };
    case SPECIFIC_CAR_DESTINATIONS:
      return {
        ...state,
        specificDestinationsLoaded: true,
      };
    case MISSIONS_LOADED:
      return {
        ...state,
        missions: action.missions,
        rentalMission: {
          ...state.rentalMission,
          ...last(filter(allPass([isRentalService, isRentalMissionOngoing]))(action.missions)),
        },
      };
    case MISSION_LOADED:
    case MISSION_CREATED:
    case MISSION_DISPATCHED:
    case MISSION_PATCHED:
      return {
        ...state,
        mission: action.mission,
      };
    case RENTAL_MISSION_CREATED:
    case RENTAL_MISSION_DISPATCHED:
      return {
        ...state,
        rentalMission: action.mission,
      };
    case RENTAL_MISSION_CANCELED:
      // No need to update 'lastCancelledMissionId', since that is used for the main mission workflow
      return {
        ...state,
        rentalMission: undefined,
      };
    case RENTAL_MISSION_UPDATED:
      return {
        ...state,
        rentalMission: { ...state.mission, ...action.mission },
      };
    case MISSION_PUTTED:
      return {
        ...state,
        mission: { ...state.mission, ...action.mission },
      };
    case MISSION_CANCELED:
      return {
        ...state,
        mission: undefined,
        lastCancelledMissionId: action.id,
      };
    case CLAIM_PATCHED:
    case CLAIM_CREATED:
    case CLAIM_LOADED:
      return {
        ...state,
        claim: action.claim,
      };
    case CLAIM_PUTTED:
      return {
        ...state,
        claim: { ...state.claim, ...action.claim },
      };
    case AUTH_SUCCESS:
    case JWT_CREATED:
      return {
        ...state,
        ...action.payload,
      };
    case CLEAR_JWT:
      return {
        ...state,
        jwt: undefined,
      };
    case NPS_ELIGIBILITY_CLAIM:
      return {
        ...state,
        ...action.payload,
      };
    case START_REQUEST:
      return {
        ...state,
        pendingRequests: state.pendingRequests + 1,
        retryCounter: assoc(
          action.request.method,
          pathOr(0, ['retryCounter', action.request.method], state) + 1,
          state.retryCounter,
        ),
      };
    case REQUEST_SUCCESS:
      return {
        ...state,
        pendingRequests: state.pendingRequests - 1,
      };
    case REQUEST_ERROR:
    case DEC_REQUEST_COUNTER:
      return {
        ...state,
        pendingRequests: state.pendingRequests - 1,
      };
    case INC_REQUEST_COUNTER:
      return {
        ...state,
        pendingRequests: state.pendingRequests + 1,
      };
    case RESET_RETRY_COUNTER:
      return {
        ...state,
        retryCounter: dissoc(action.request.method, state.retryCounter),
      };
    case RETRY_NOT_FOUND_MODAL:
      return {
        ...state,
        retryNotFoundModal: not(state.retryNotFoundModal),
      };
    case RETRY_EXPIRED_MODAL:
      return {
        ...state,
        retryExpiredModal: not(state.retryExpiredModal),
      };
    case RETRY_CLAIM_EXIST:
      return {
        ...state,
        retryClaimExist: not(state.retryClaimExist),
      };
    case HIGHWAY_SECTIONS_FOUND:
      return {
        ...state,
        highwaySections: action.sections,
      };
    case VEHICLE_INFO_LOADED:
      return assocPath(['claim', 'vehicle'], action.vehicle, state);
    case CLEAR_VEHICLE_DESTINATION_VALUES:
      return {
        ...state,
        mission: omit(['vehicleDestination'], state.mission),
      };
    case PRODUCT_CONFIGURATION_LOADED:
      return { ...state, productConfiguration: action.configuration };
    case CLAIM_HISTORY_DISPATCH: {
      const claimHistory = action.history?.claimHistory ? extractUniqueClaimHistory(action.history?.claimHistory) : [];
      const missionHistory = action.history?.missionHistory;

      const missionHistoryForTimeline = missionHistory ? extractMissionHistory(missionHistory, state) : [];

      const extractedInformationForRentalMission = extractRentalMissionAdditionalInformationFromHistoryEvent(
        missionHistory,
        state,
      );

      return {
        ...state,
        claimHistoryCurrent: { claimHistory, missionHistory: missionHistoryForTimeline },
        rentalMission: extractedInformationForRentalMission
          ? mergeDeepRight(state.rentalMission, extractedInformationForRentalMission)
          : state.rentalMission,
      };
    }
    case SELECT_BENEFIT:
      return {
        ...state,
        selectedBenefit: action.payload,
      };
    case CSC_ELIGIBILE:
      return {
        ...state,
        cscEligibility: action.response,
      };
    case RENTAL_CURRENT_LIMITS:
      return {
        ...state,
        rentalCurrentLimits: action.response,
      };
    default:
      return state;
  }
};

export const decRequestCounter = () => ({ type: DEC_REQUEST_COUNTER });
export const incRequestCounter = () => ({ type: INC_REQUEST_COUNTER });
export const startRequest = request => ({ type: START_REQUEST, request });
export const requestError = ({ method, errorCode, statusCode, errorMessage }) => ({
  type: REQUEST_ERROR,
  error: { method, errorCode, statusCode, errorMessage },
});
export const requestSuccess = request => ({ type: REQUEST_SUCCESS, request });
export const resetRetryCounter = (request = {}) => ({ type: RESET_RETRY_COUNTER, request });

export const retryNotFoundModal = () => dispatch => dispatch({ type: RETRY_NOT_FOUND_MODAL });
export const retryExpiredModal = () => dispatch => dispatch({ type: RETRY_EXPIRED_MODAL });
export const retryClaimExist = () => dispatch => dispatch({ type: RETRY_CLAIM_EXIST });

export const clearMissionMonitoring = () => dispatch => dispatch({ type: CLEAR_MISSION_MONITORING });

export const cancelAppointment = () => ({ type: CANCEL_APPOINTMENT });
export const updateAppointment = () => ({ type: UPDATE_APPOINTMENT });

export const continueProcess = () => ({ type: CONTINUE_PROCESS });

export const cancelMilestone = () => ({ type: CANCEL_MILESTONE });
export const cancelHighwayDirection = () => ({ type: CANCEL_HIGHWAY_DIRECTION });

export const anotherRequest = () => ({ type: ANOTHER_REQUEST });
export const cancelPreviousClaimHandling = () => ({ type: CANCEL_PREVIOUS_CLAIM_HANDLING });
export const rentalBenefitCoverageIssue = () => ({ type: RENTAL_BENEFIT_COVERAGE_ISSUE });
export const rentalMissionAlreadyExists = () => ({ type: RENTAL_MISSION_ALREADY_EXISTS });

const requestCoreApi = (dispatch, ctx, { errorCode, refreshAuthToken = false } = {}) => {
  const { method } = ctx;
  dispatch(startRequest({ method }));
  return coreApi(ctx)
    .then(res => {
      dispatch(resetRetryCounter({ method }));
      dispatch(requestSuccess({ method }));
      return res;
    })
    .catch(err => {
      if (err.response) {
        if (err.response.status === 401 && refreshAuthToken) {
          dispatch(decRequestCounter());
          return dispatch(refreshToken())
            .then(auth => {
              return requestCoreApi(dispatch, { ...ctx, auth }, { refreshAuthToken: false });
            })
            .catch(() => {
              piwik.trackEvent(eventTags.session_error);
              dispatch(sessionError());
            });
        }
        const {
          data: { errorCode: resErrorCode, errorMessage: resErrorMessage },
          status,
        } = err.response;

        piwik.trackEvent({ ...eventTags.technical_error, event_label: resErrorCode });

        dispatch(
          requestError({
            method,
            errorCode: errorCode || resErrorCode,
            statusCode: status,
            errorMessage: resErrorMessage,
          }),
        );
      } else {
        piwik.trackEvent({ ...eventTags.technical_error, event_label: errorCode });
        dispatch(requestError({ method: ctx.method, errorCode }));
      }
      throw err;
    });
};

const sessionError = () => ({ type: SESSION_ERROR });

const authSuccess = (payload = {}) => (dispatch, getState) => {
  const { access_token, refresh_token, token_type, scope, expires_in } = payload; // eslint-disable-line camelcase
  const expiresAt = new Date(new Date().getTime() + expires_in * 1000); // eslint-disable-line camelcase
  const data = {
    accessToken: access_token,
    tokenType: token_type,
    refreshToken: refresh_token,
    scope,
    expiresAt,
  };
  dispatch({ type: AUTH_SUCCESS, payload: data });
  const state = getState();
  const session = getSession(state);
  const auth = getAuth(state);
  webStorage.setAuth(session, auth);
  return auth;
};

const SCOPES = { APPROVED, AUTHENTICATED, IDENTIFIED };
const getScopeType = scope => SCOPES[scope];

export const authenticate = () => (dispatch, getState) => {
  return requestCoreApi(
    dispatch,
    { method: coreApi.endpoints.authenticate },
    { errorCode: errors.AUTH_ERROR, refreshAuthToken: false },
  ).then(payload => {
    dispatch(authSuccess(payload));
    const scope = getScopeStatus(getState());
    if (!scope) throw new Error('Unknow scope');
    dispatch({ type: getScopeType(scope) });
  });
};

export const refreshToken = () => (dispatch, getState) => {
  const state = getState();
  const auth = {
    refreshToken: getRefreshToken(state),
  };
  return requestCoreApi(dispatch, { method: coreApi.endpoints.refreshToken, auth }, { refreshAuthToken: false }).then(
    payload => {
      const scope = getScopeStatus(getState());
      if (!scope) return dispatch(requestError({ method: coreApi.endpoints.refreshToken }));
      dispatch(authSuccess(payload));
      return getAuth(getState());
    },
  );
};

export const createClaimV1 = () => (dispatch, getState) => {
  const state = getState();
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);
  const settings = getSettings(state);

  const isVinMandatory = settings.ELIGIBILITYCHECK_CAR_VIN_DISPLAYED && settings.VIN_MANDATORY;

  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };
  let data = {
    leaserContractNumber: getLeaserContractNumber(state),
    vehicle: {
      mileage: Number(getMileage(state)) || undefined,
      plateNumber: getPlateNumber(state),
      vin: getVinNumber(state) || undefined,
    },
    driver: {
      firstName: getFirstName(state),
      lastName: getLastName(state),
      phoneMobile1: getPhoneNumber(state),
    },
    isVinMandatory,
  };
  if (getBrand(state)) {
    data = {
      ...data,
      vehicle: {
        ...data.vehicle,
        country: getCountry(state),
        make: getBrand(state),
        model: getModel(state),
        type: getType(state),
        weight: getWeight(state),
        registrationDate: getRegistrationDate(state),
      },
    };
  }
  if (isApiPublicV5) {
    data = {
      ...data,
      vehicle: {
        ...data.vehicle,
        energy: getEnergy(state),
        energyType: getEnergyType(state),
      },
    };
  }
  let method = coreApi.endpoints.createClaimV1;
  if (isApiPublicV4) method = coreApi.endpoints.createClaimV4;
  if (isApiPublicV5) method = coreApi.endpoints.createClaimV5;

  return requestCoreApi(dispatch, { method, data, auth }).then(claim => {
    dispatch({ type: CLAIM_CREATED, claim });
    const updatedState = getState();
    const newClaim = getClaim(updatedState);
    const session = getSession(updatedState);
    webStorage.setClaimId(session, newClaim);
    return newClaim;
  });
};

export const putClaimLocation = () => (dispatch, getState) => {
  const state = getState();
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);
  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };

  let method = coreApi.endpoints.putClaimLocation;
  if (isApiPublicV4) method = coreApi.endpoints.putClaimLocationV4;
  if (isApiPublicV5) method = coreApi.endpoints.putClaimLocationV5;

  const claimId = getClaimId(state);
  const location = getLocation(state);
  const position = getClaimPosition(state);
  const address = getClaimAddress(state);
  const data = { position, address, ...location };
  dispatch({ type: PUT_CLAIM });
  return requestCoreApi(dispatch, {
    method,
    claimId,
    data,
    auth,
  }).then(() => dispatch({ type: CLAIM_PUTTED, claim: { location: data } }));
};

export const searchClaim = (plateNumber, vin, phoneNumber) => (dispatch, getState) => {
  const state = getState();
  const jwt = getJwt(state);
  const auth = { accessToken: getAccessToken(state), jwt };
  let method = coreApi.endpoints.searchClaimV1;
  if (getIsPublicApiV4(state)) method = coreApi.endpoints.searchClaimV4;
  if (getIsPublicApiV5(state)) method = coreApi.endpoints.searchClaimV5;

  return requestCoreApi(dispatch, {
    method,
    data: { plateNumber, vin, phoneNumber },
    auth,
  });
};

export const npsEligibilityClaim = () => (dispatch, getState) => {
  const state = getState();
  const claimId = getClaimId(state);
  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };
  let method = coreApi.endpoints.npsEligibilityClaim;
  if (getIsPublicApiV5(state)) method = coreApi.endpoints.npsEligibilityClaimV5;

  return requestCoreApi(dispatch, { method, claimId, auth }).then(npsConsent => {
    dispatch({ type: NPS_ELIGIBILITY_CLAIM, payload: { npsConsent } });
    return npsConsent;
  });
};

export const putNpsEligibilityClaim = () => (dispatch, getState) => {
  const state = getState();
  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };
  let method = coreApi.endpoints.putNpsEligibilityClaimV4;
  if (getIsPublicApiV5(state)) method = coreApi.endpoints.putNpsEligibilityClaimV5;

  const claimId = getClaimId(state);
  const npsAnswer = getNpsAnswer(state);
  if (!claimId) return;
  const data = {
    npsAnswer,
  };
  return requestCoreApi(dispatch, { method, data, claimId, auth }).then(() =>
    dispatch({ type: PUT_NPS_ELIGIBILITY_CLAIM }),
  );
};

export const searchClaimByIdV1 = id => (dispatch, getState) => {
  const state = getState();
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);

  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };

  let method = coreApi.endpoints.searchClaimByIdV1;
  if (isApiPublicV4) method = coreApi.endpoints.searchClaimByIdV4;
  if (isApiPublicV5) method = coreApi.endpoints.searchClaimByIdV5;

  return requestCoreApi(dispatch, { method, data: { id }, auth });
};

export const loadClaimById = () => async (dispatch, getState) => {
  const state = getState();
  let session = getSession(state);
  const claimId = getPreviousClaimId(state) || getClaimId(state);
  const claim = await dispatch(searchClaimByIdV1(claimId));
  dispatch({ type: CLAIM_LOADED, claim });
  const updatedState = getState();
  const newClaim = getClaim(updatedState);
  session = getSession(updatedState);
  webStorage.setClaimId(session, newClaim);
  return claim;
};

export const loadClaimByPlateNumber = () => (dispatch, getState) => {
  const state = getState();
  const settings = getSettings(state);
  let session = getSession(state);
  const plateNumber = getPlateNumber(state);

  const shouldSendVinNumber = settings.ELIGIBILITYCHECK_CAR_VIN_DISPLAYED && settings.VIN_MANDATORY;
  const vin = shouldSendVinNumber ? getVinNumber(state) : null;
  return dispatch(searchClaim(plateNumber, vin)).then(claims => {
    if (!claims.numberOfElements) return dispatch({ type: NO_CLAIM_FOUND });
    const claim = claims.content[0];
    if (differenceInDays(new Date(), new Date(claim.creationDate)) > 7) return dispatch({ type: NO_CLAIM_FOUND });
    dispatch({ type: CLAIM_LOADED, claim });
    const updatedState = getState();
    const newClaim = getClaim(updatedState);
    session = getSession(updatedState);
    webStorage.setClaimId(session, newClaim);
    return claim;
  });
};

export const searchMissionV1 = () => (dispatch, getState) => {
  const state = getState();
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);

  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };

  let method = coreApi.endpoints.getAllMissions;
  if (isApiPublicV4) method = coreApi.endpoints.getAllMissionsV4;
  if (isApiPublicV5) method = coreApi.endpoints.getAllMissionsV5;

  const claim = getClaim(state);
  return requestCoreApi(dispatch, { method, claimId: claim.id, auth }).then(missions => {
    const missionTowOrRos = lastMissionTowOrRos(missions);
    if (missions && missions.length) {
      dispatch({ type: MISSIONS_LOADED, missions });
      if (missionTowOrRos) dispatch({ type: MISSION_LOADED, mission: missionTowOrRos });
      else dispatch({ type: NO_MISSION_FOUND });
    } else dispatch({ type: NO_MISSION_FOUND });
  });
};

export const searchMissionByPreviousIdV1 = () => (dispatch, getState) => {
  const state = getState();
  const previousMissionId = getPreviousMissionId(state);
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);

  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };

  let method = coreApi.endpoints.getAllMissions;
  if (isApiPublicV4) method = coreApi.endpoints.getAllMissionsV4;
  if (isApiPublicV5) method = coreApi.endpoints.getAllMissionsV5;

  const claim = getClaim(state);
  return requestCoreApi(dispatch, { method, claimId: claim.id, auth }).then(missions => {
    const myMission = find(propEq('id', previousMissionId))(missions);
    if (missions && missions.length && myMission) dispatch({ type: MISSION_LOADED, mission: myMission });
    else dispatch({ type: NO_MISSION_FOUND });
  });
};

export const loadCarDestinationsInfo = () => (dispatch, getState) => {
  const state = getState();
  const mission = getMission(state);
  return Promise.all([dispatch(getCarDestinations(mission)), dispatch(getCarDestinationsRules(mission))]).then(() =>
    dispatch({ type: CAR_DESTINATIONS_INFO_LOADED }),
  );
};

export const createMissionV1 = () => (dispatch, getState) => {
  const state = getState();
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);

  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };
  let method = coreApi.endpoints.createMissionV1;
  if (isApiPublicV4) method = coreApi.endpoints.createMissionV4;
  if (isApiPublicV5) method = coreApi.endpoints.createMissionV5;
  const claimId = getClaimId(state);
  dispatch({ type: CREATE_MISSION });
  const data = {
    missionType: MISSION_TYPE.IMMEDIATE,
  };
  return requestCoreApi(dispatch, { method, claimId, data, auth }).then(mission => {
    if (isTOWMission(mission)) {
      return Promise.all([dispatch(getCarDestinations(mission)), dispatch(getCarDestinationsRules(mission))]).then(
        () => {
          dispatch({ type: MISSION_CREATED, mission });
          return mission;
        },
      );
    }

    if (isROSMission(mission) && (isApiPublicV4 || isApiPublicV5)) {
      return dispatch(getCarDestinationsRules(mission)).then(carDestinationsRules => {
        if (carDestinationsRules?.limits?.allowHidingDestinationProcessInDigital) {
          return dispatch(getCarDestinations(mission)).then(() => {
            dispatch({ type: CAR_DESTINATIONS_RULES, rules: carDestinationsRules });
            dispatch({ type: MISSION_CREATED, mission });
            return mission;
          });
        }
        dispatch({ type: MISSION_CREATED, mission });
        return mission;
      });
    }

    dispatch({ type: MISSION_CREATED, mission });
    return mission;
  });
};

export const createRentalMission = () => (dispatch, getState) => {
  const state = getState();

  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);

  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };

  let method;
  if (isApiPublicV4) method = coreApi.endpoints.createRentalMissionV4;
  if (isApiPublicV5) method = coreApi.endpoints.createRentalMissionV5;

  const claimId = getClaimId(state);
  dispatch({ type: CREATE_RENTAL_MISSION });
  const data = {
    missionType: MISSION_TYPE.IMMEDIATE,
    benefitCode: 'REPLACEMENT_CAR',
  };

  return requestCoreApi(dispatch, { method, claimId, data, auth }).then(mission => {
    dispatch({ type: RENTAL_MISSION_CREATED, mission });
    return mission;
  });
};

export const updateRentalMission = () => (dispatch, getState) => {
  const state = getState();
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);

  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };

  let method;
  if (isApiPublicV4) method = coreApi.endpoints.updateRentalMissionV4;
  if (isApiPublicV5) method = coreApi.endpoints.updateRentalMissionV5;

  const claimId = getClaimId(state);
  const missionId = getCoreRentalMissionId(state);
  const mission = {
    ...getCoreRentalMission(state),
    rentalDrivers: getRentalDrivers(state),
    rentalVehicleDetails: getRentalVehicleDetails(state),
    rentalBookingDetails: getRentalBookingDetails(state),
  };

  const data = {
    rentalDrivers: getRentalDrivers(state),
    rentalVehicleDetails: getRentalVehicleDetails(state),
    rentalBookingDetails: getRentalBookingDetails(state),
  };

  return requestCoreApi(dispatch, { method, claimId, missionId, data, auth }).then(() =>
    dispatch({ type: RENTAL_MISSION_UPDATED, mission }),
  );
};

export const extendRentalMission = () => (dispatch, getState) => {
  const state = getState();

  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };

  const method = coreApi.endpoints.extendRentalMission;
  const version = getIsPublicApiV5(state) ? PUBLIC_API_VERSION.V5 : PUBLIC_API_VERSION.V4;

  const claimId = getClaimId(state);
  const missionId = getCoreRentalMissionId(state);
  const data = {
    durationInMinutes: getRentalExtensionDurationInMinutes(state),
  };

  return requestCoreApi(dispatch, { method, version, claimId, missionId, data, auth }).then(
    response => !response.errorCode && dispatch({ type: RENTAL_EXTENSION_SENT }),
  );
};

const getCarDestinations = mission => (dispatch, getState) => {
  const state = getState();
  let method = coreApi.endpoints.getCarDestinationsV1;
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);

  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };

  if (isApiPublicV4) method = coreApi.endpoints.getCarDestinationsV4;
  if (isApiPublicV5) method = coreApi.endpoints.getCarDestinationsV5;

  const claim = getClaim(state);
  dispatch({ type: REQUEST_CAR_DESTINATIONS });
  return requestCoreApi(dispatch, {
    method,
    missionId: mission.id,
    claimId: claim.id,
    auth,
  })
    .then(destinations => {
      dispatch({ type: CAR_DESTINATIONS, destinations });
      return destinations;
    })
    .catch(err => {
      if (err.response.status === CAR_DESTINATION_NOT_FOUND) {
        return dispatch({ type: CAR_DESTINATIONS, destinations: [] });
      }
      throw err;
    });
};

export const getSpecificCarDestinations = data => (dispatch, getState) => {
  const state = getState();

  const claim = getClaim(state);
  const mission = getMission(state);

  let method = coreApi.endpoints.getSpecificCarDestinationsV4;
  const isApiPublicV5 = getIsPublicApiV5(state);
  if (isApiPublicV5) method = coreApi.endpoints.getSpecificCarDestinationsV5;

  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };

  dispatch({ type: REQUEST_SPECIFIC_CAR_DESTINATIONS });

  return requestCoreApi(dispatch, {
    method,
    data,
    claimId: claim.id,
    missionId: mission.id,
    auth,
  })
    .then(destinations => {
      dispatch({ type: CAR_DESTINATIONS, destinations });
      dispatch({ type: SPECIFIC_CAR_DESTINATIONS, destinations });
      return destinations;
    })
    .catch(err => {
      if (err.response.status === CAR_DESTINATION_NOT_FOUND) {
        return dispatch({ type: SPECIFIC_CAR_DESTINATIONS, destinations: [] });
      }
      throw err;
    });
};

const getCarDestinationsRules = mission => (dispatch, getState) => {
  const state = getState();
  let towMethod = coreApi.endpoints.getTowLimits;
  let rosMethod;
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);

  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };

  if (isApiPublicV4) {
    towMethod = coreApi.endpoints.getTowLimitsV4;
    rosMethod = coreApi.endpoints.getRosLimitsV4;
  }
  if (isApiPublicV5) {
    towMethod = coreApi.endpoints.getTowLimitsV5;
    rosMethod = coreApi.endpoints.getRosLimitsV5;
  }

  const claim = getClaim(state);
  dispatch({ type: REQUEST_CAR_DESTINATIONS_RULES });
  if (isTOWMission(mission))
    return requestCoreApi(dispatch, {
      method: towMethod,
      missionId: mission.id,
      claimId: claim.id,
      auth,
    }).then(rules => {
      dispatch({ type: CAR_DESTINATIONS_RULES, rules });
      return rules;
    });
  if (isROSMission(mission))
    return requestCoreApi(dispatch, {
      method: rosMethod,
      missionId: mission.id,
      claimId: claim.id,
      auth,
    }).then(rules => {
      return rules;
    });
};

const endMissionMonitoring = () => (dispatch, getState) => {
  const state = getState();
  const eventSource = getEventSource(state);
  eventSource.close();
};

export const missionHistoryState = (state, data) => {
  let parsingObject = {};
  let payload = {};
  let lastEtaUpdate = {};
  let newData = map(param => ({ ...param, timestamp: new Date(param.timestamp) }), data);

  newData = sortBy(prop('timestamp'), newData);
  return newData.reduce((previousValue, currentValue) => {
    if (currentValue?.payload?.eta) {
      lastEtaUpdate = new Date(currentValue?.timestamp).toISOString();
    }

    const rentalMissionId = getCoreRentalMissionId(state);
    if (rentalMissionId && rentalMissionId === currentValue.missionId) {
      const rentalMission = merge(previousValue?.rentalMission?.payload, currentValue.payload);

      return {
        ...previousValue,
        rentalMission: {
          ...previousValue?.rentalMission,
          ...rentalMission,
          ...extractRentalMissionAdditionalInformationFromMonitoringEvent(currentValue),
        },
      };
    }

    if (previousValue?.mainMission?.missionId !== currentValue.missionId) {
      payload = merge(payload, currentValue.payload);
      parsingObject = merge(parsingObject, currentValue);
    } else {
      payload = merge(previousValue.payload, currentValue.payload);
      parsingObject = merge(parsingObject, currentValue);
    }

    if (isEmpty(payload.currentLocation)) {
      delete payload.currentLocation;
    }

    return {
      ...previousValue,
      mainMission: {
        ...previousValue?.mainMission,
        ...parsingObject,
        payload: {
          ...previousValue?.mainMission?.payload,
          ...payload,
          lastEtaUpdate,
        },
      },
    };
  }, {});
};

const extractRentalMissionAdditionalInformationFromHistoryEvent = (missionHistory, state) => {
  const acceptedHistoryItem = missionHistory?.find(
    mission => mission.type === HISTORY_EVENTS.MISSION_ACCEPTED && mission.missionId === state?.rentalMission?.id,
  );
  return acceptedHistoryItem ? { requestAcceptedTimestamp: acceptedHistoryItem.timestamp } : {};
};

const extractRentalMissionAdditionalInformationFromMonitoringEvent = (event, state) => {
  if (!event?.payload?.status && state?.rentalMission?.id === event.missionId) {
    return {};
  }

  switch (event.payload.status) {
    case MISSION_STATUS.PENDING:
      return { requestSentTimestamp: event.timestamp };
    case MISSION_STATUS.ACCEPTED:
      return { requestAcceptedTimestamp: event.timestamp };
    default:
      return {};
  }
};

const mapMissionMonitoringEvent = (state, event) => {
  const rentalMissionId = getCoreRentalMission(state)?.id;
  if (rentalMissionId === event.payload.id) {
    return {
      rentalMission: {
        ...event.payload,
        ...extractRentalMissionAdditionalInformationFromMonitoringEvent(event, state),
      },
    };
  }

  const payload = {};

  if (event?.payload?.eta) {
    payload.lastEtaUpdate = event.timestamp;
  }

  return { mainMission: { ...event.payload, payload: { ...event.payload, ...payload } } };
};

export const startMissionMonitoring = () => (dispatch, getState) => {
  const state = getState();

  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);
  let method = coreApi.endpoints.startMissionMonitoring;
  if (isApiPublicV4) method = coreApi.endpoints.startMissionMonitoringV4;
  if (isApiPublicV5) method = coreApi.endpoints.startMissionMonitoringV5;
  const onError = () => {
    dispatch(requestError({ method }));
  };
  let endOfMissionTimer;
  const resetEndOfMissionTimeout = ({ eta }, timer, fn) => {
    if (timer) clearTimeout(timer);
    return setTimeout(fn, new Date(eta) - Date.now());
  };
  const onMessage = data => {
    if (is(Array, data)) {
      if (data.length) {
        const mergedData = missionHistoryState(state, data);
        const payload = mergedData?.mainMission?.payload;
        if (payload?.eta)
          endOfMissionTimer = resetEndOfMissionTimeout(payload, endOfMissionTimer, () =>
            dispatch({
              type: MISSION_MONITORING_EVENT,
              id: -1,
              timestamp: new Date(),
              payload: {
                mainMission: { status: MISSION_STATUS.ETA_EXPIRED },
              },
            }),
          );
        dispatch({ type: MISSION_MONITORING_EVENT, payload: mergedData });
      }
    } else {
      const { id, timestamp, payload } = data;
      if (payload.eta)
        endOfMissionTimer = resetEndOfMissionTimeout(payload, endOfMissionTimer, () =>
          dispatch({
            type: MISSION_MONITORING_EVENT,
            id: -1,
            timestamp: new Date(),
            payload: {
              mainMission: { status: MISSION_STATUS.ETA_EXPIRED },
            },
          }),
        );
      if (endOfMissionTimer && isMissionMonitoringIsEnded(payload.status)) clearTimeout(endOfMissionTimer);

      const mappedPayload = mapMissionMonitoringEvent(state, data);
      dispatch({ type: MISSION_MONITORING_EVENT, id, timestamp, payload: mappedPayload });
    }
  };
  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };
  const { id: claimId } = getClaim(state);
  const ctx = {
    method,
    claimId,
    auth,
    onMessage,
    onError,
  };
  const eventSource = coreApi(ctx);
  dispatch({ type: MISSION_MONITORING_STARTED, eventSource });
};

export const clearVehicleDestinationValues = () => dispatch => dispatch({ type: CLEAR_VEHICLE_DESTINATION_VALUES });

export const putVehicleDestination = () => (dispatch, getState) => {
  const state = getState();
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);
  let method = coreApi.endpoints.putVehicleDestination;
  if (isApiPublicV4) method = coreApi.endpoints.putVehicleDestinationV4;
  if (isApiPublicV5) method = coreApi.endpoints.putVehicleDestinationV5;
  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };
  const recommendedCarDestinations = getRecommendedCarDestinations(state);
  const isDestinationProcessHidden = getAllowHidingDestinationProcessInDigital(state);
  let vehicleDestination = {
    type: VEHICLE_DESINATION_TYPE.NONE,
    reason: VEHICLE_DESINATION_REASON.NO_NETWORK_AVAILABLE,
  };
  if (!isEmpty(getCarDestination(state))) vehicleDestination = getCarDestination(state);
  if (isDestinationProcessHidden && !isEmpty(recommendedCarDestinations))
    vehicleDestination = { type: VEHICLE_DESINATION_TYPE.RECOMMENDED, id: recommendedCarDestinations[0].id };
  const data = { vehicleDestination };
  const providerId = vehicleDestination?.id;
  const missionVehicleDestination = {
    vehicleDestination: !isNil(providerId)
      ? {
          type: VEHICLE_DESINATION_TYPE.RECOMMENDED,
          provider: { ...find(propEq('id', providerId), recommendedCarDestinations) },
        }
      : { type: vehicleDestination.type, provider: { ...pick(['name', 'location'], vehicleDestination) } },
  };
  const claimId = getClaimId(state);
  const missionId = Number(getMissionId(state));
  return requestCoreApi(dispatch, {
    method,
    data,
    missionId,
    claimId,
    auth,
  }).then(() =>
    dispatch({
      type: MISSION_PUTTED,
      mission: missionVehicleDestination,
    }),
  );
};

export const cancelMission = () => (dispatch, getState) => {
  const state = getState();
  let method = coreApi.endpoints.cancelMissionV1;
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);

  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };

  if (isApiPublicV4) method = coreApi.endpoints.cancelMissionV4;
  if (isApiPublicV5) method = coreApi.endpoints.cancelMissionV5;

  const claimId = getClaimId(state);
  const missionId = getMissionId(state);
  dispatch({ type: CANCEL_MISSION });
  return requestCoreApi(dispatch, {
    method,
    claimId,
    missionId,
    auth,
  }).then(data => {
    dispatch({ type: MISSION_CANCELED });
    return data;
  });
};

export const cancelMissionCost = missionId => async (dispatch, getState) => {
  const state = getState();

  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);

  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };

  let method;

  if (isApiPublicV4) {
    method = coreApi.endpoints.cancelMissionCostV4;
  }

  if (isApiPublicV5) {
    method = coreApi.endpoints.cancelMissionCostV5;
  }

  const claimId = getClaimId(state);

  const data = await requestCoreApi(dispatch, {
    method,
    claimId,
    missionId,
    auth,
  });

  return data;
};

export const automaticCancelMission = missionIdToCancel => async (dispatch, getState) => {
  const state = getState();
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);

  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };

  let method;

  if (isApiPublicV4) {
    method = coreApi.endpoints.automaticCancelMissionV4;
  }

  if (isApiPublicV5) {
    method = coreApi.endpoints.automaticCancelMissionV5;
  }

  const claimId = getClaimId(state);
  const missionId = missionIdToCancel || getMissionId(state);

  dispatch({ type: CANCEL_MISSION });

  const isRentalMission = missionId === getCoreRentalMissionId(state);

  return requestCoreApi(dispatch, {
    method,
    claimId,
    missionId,
    auth,
  }).then(data => {
    dispatch({
      type: isRentalMission ? RENTAL_MISSION_CANCELED : MISSION_CANCELED,
      id: missionId,
    });
    return data;
  });
};

export const dispatchMissionV1 = () => (dispatch, getState) => {
  const state = getState();
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);

  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };

  let method = coreApi.endpoints.dispatchMission;
  if (isApiPublicV4) method = coreApi.endpoints.dispatchMissionV4;
  if (isApiPublicV5) method = coreApi.endpoints.dispatchMissionV5;

  const claimId = getClaimId(state);
  const mission = getMission(state);
  return requestCoreApi(dispatch, {
    method,
    missionId: mission.id,
    claimId,
    auth,
  }).then(() => dispatch({ type: MISSION_DISPATCHED, mission }));
};

export const dispatchRentalMissionV1 = () => (dispatch, getState) => {
  const state = getState();
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);

  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };

  let method = coreApi.endpoints.dispatchMission;
  if (isApiPublicV4) method = coreApi.endpoints.dispatchMissionV4;
  if (isApiPublicV5) method = coreApi.endpoints.dispatchMissionV5;

  const claimId = getClaimId(state);
  const mission = getCoreRentalMission(state);
  return requestCoreApi(dispatch, {
    method,
    missionId: mission.id,
    claimId,
    auth,
  }).then(() => dispatch({ type: RENTAL_MISSION_DISPATCHED, mission }));
};

export const updateMissionV1 = () => (dispatch, getState) => {
  const state = getState();
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);

  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };

  const recommendedCarDestinations = getRecommendedCarDestinations(state);
  const isDestinationProcessHidden = getAllowHidingDestinationProcessInDigital(state);
  let vehicleDestination = {
    type: VEHICLE_DESINATION_TYPE.NONE,
    reason: VEHICLE_DESINATION_REASON.NO_NETWORK_AVAILABLE,
  };
  if (!isEmpty(getCarDestination(state))) vehicleDestination = getCarDestination(state);
  if (isDestinationProcessHidden && !isEmpty(recommendedCarDestinations)) {
    vehicleDestination = { type: VEHICLE_DESINATION_TYPE.RECOMMENDED, id: recommendedCarDestinations[0].id };
  }

  const data = {
    vehicleDestination,
  };

  let method;
  if (isApiPublicV4) method = coreApi.endpoints.updateMissionV4;
  if (isApiPublicV5) method = coreApi.endpoints.updateMissionV5;

  const claimId = getClaimId(state);
  const mission = {
    ...getMission(state),
    vehicleDestination: !isNil(vehicleDestination?.id)
      ? {
          type: VEHICLE_DESINATION_TYPE.RECOMMENDED,
          provider: { ...find(propEq('id', vehicleDestination?.id), recommendedCarDestinations) },
        }
      : { type: vehicleDestination.type, provider: { ...pick(['name', 'location'], vehicleDestination) } },
  };
  return requestCoreApi(dispatch, {
    method,
    missionId: mission.id,
    claimId,
    data,
    auth,
  }).then(() => dispatch({ type: MISSION_PUTTED, mission }));
};

export const digitalInteractionsV1 = () => (dispatch, getState) => {
  const state = getState();
  const settings = getSettings(state);

  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);
  let method = coreApi.endpoints.digitalInteractionsV1;
  if (isApiPublicV4) method = coreApi.endpoints.digitalInteractionsV4;
  if (isApiPublicV5) method = coreApi.endpoints.digitalInteractionsV5;

  const shouldSendVinNumber = settings.ELIGIBILITYCHECK_CAR_VIN_DISPLAYED && settings.VIN_MANDATORY;
  const vin = shouldSendVinNumber ? getVinNumber(state) : null;

  const data = {
    callback: true,
    driver: {
      firstName: getFirstName(state),
      lastName: getLastName(state),
      contact: {
        phoneMobile1: getPhoneNumber(state),
      },
    },
    vehicle: {
      plateNumber: getPlateNumber(state),
      country: getLocationCountry(state),
      ...(vin && { vin }),
    },
    event: {
      ...getEvent(state),
      ...getEventDetails(state),
    },
    location: getLocation(state),
  };
  return requestCoreApi(dispatch, {
    method,
    data,
    auth,
  }).then(() => {
    dispatch({ type: DIGITAL_INTERACTIONS });
  });
};

export const getBrandSuggestions = (hint, setSuggestions) => (dispatch, getState) => {
  const state = getState();
  let method = coreApi.endpoints.getBrandSuggestionsV1;
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);

  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };
  if (isApiPublicV4) method = coreApi.endpoints.getBrandSuggestionsV4;
  if (isApiPublicV5) method = coreApi.endpoints.getBrandSuggestionsV5;

  return requestCoreApi(dispatch, { method, auth, hint }).then(results => {
    const suggestions = map(result => ({ label: result }), results);
    return setSuggestions(suggestions);
  });
};

export const getModelSuggestions = (brand, hint, setSuggestions) => (dispatch, getState) => {
  const state = getState();
  let method = coreApi.endpoints.getModelSuggestionsV1;
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);

  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };
  if (isApiPublicV4) method = coreApi.endpoints.getModelSuggestionsV4;
  if (isApiPublicV5) method = coreApi.endpoints.getModelSuggestionsV5;

  return requestCoreApi(dispatch, { method, auth, brand, hint }).then(results => {
    const suggestions = map(({ model, type, weight }) => ({ label: model, type, weight }), results);
    return setSuggestions(suggestions);
  });
};

export const getHighwaySections = () => (dispatch, getState) => {
  const state = getState();
  let method = coreApi.endpoints.getHighwaySectionsV1;
  const isApiPublicV1 = getIsPublicApiV1(state);
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);

  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };
  if (isApiPublicV4) method = coreApi.endpoints.getHighwaySectionsV4;
  if (isApiPublicV5) method = coreApi.endpoints.getHighwaySectionsV5;

  let queryValues;
  if (isEligible(state) && !isApiPublicV1) {
    const { lat, lng } = getClaimPosition(state);
    const metres = getClaimHighwayMetres(state);
    queryValues = { lat, lng, metres };
  } else {
    const { lat, lng } = getPosition(state);
    const metres = getHighwayMetres(state);
    queryValues = { lat, lng, metres };
  }
  return requestCoreApi(dispatch, { method, ...queryValues, auth }).then(sections => {
    if (isEmpty(sections)) return dispatch({ type: HIGHWAY_SECTIONS_NOT_FOUND });
    dispatch({ type: HIGHWAY_SECTIONS_FOUND, sections });
    return sections;
  });
};

export const getVehicleInfo = () => (dispatch, getState) => {
  const state = getState();
  const settings = getSettings(state);

  const isVinMandatory = settings.ELIGIBILITYCHECK_CAR_VIN_DISPLAYED && settings.VIN_MANDATORY;
  let method = coreApi.endpoints.searchVehicleInfoV1;
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);
  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };
  if (isApiPublicV4) method = coreApi.endpoints.searchVehicleInfoV4;
  if (isApiPublicV5) method = coreApi.endpoints.searchVehicleInfoV5;
  const plateNumber = getPlateNumber(state);
  const vin = getVinNumber(state);
  return requestCoreApi(dispatch, { method, plateNumber, vin, isVinMandatory, auth }).then(vehicle => {
    dispatch({ type: VEHICLE_INFO_LOADED, vehicle });
  });
};

export const putClaimVehicleLoss = () => (dispatch, getState) => {
  const state = getState();
  let method = coreApi.endpoints.putClaimVehicleLossV1;
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);
  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };
  if (isApiPublicV4) method = coreApi.endpoints.putClaimVehicleLossV4;
  if (isApiPublicV5) method = coreApi.endpoints.putClaimVehicleLossV5;
  const claimId = getClaimId(state);
  const { CUSTOMER_WILL_ANSWER_LATER } = FNOL_CUSTOMER_ANSWER;
  const defaultData = {
    customerAnswer: CUSTOMER_WILL_ANSWER_LATER,
    callback: true,
  };
  const fnol = getFnol(state);
  const data = isEmpty(fnol) ? defaultData : fnol;
  return requestCoreApi(dispatch, { method, claimId, data, auth }).then(() =>
    dispatch({ type: CLAIM_VEHICLE_LOSS_PUTTED }),
  );
};

export const getInsuranceCompanies = name => (dispatch, getState) => {
  const state = getState();
  let method = coreApi.endpoints.getInsuranceCompaniesV1;
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);
  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };
  if (isApiPublicV4) method = coreApi.endpoints.getInsuranceCompaniesV4;
  if (isApiPublicV5) method = coreApi.endpoints.getInsuranceCompaniesV5;
  return requestCoreApi(dispatch, { method, name, auth });
};

export const createClaimDigitalExit = customCode => () => (dispatch, getState) => {
  const state = getState();
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);
  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };
  let method = coreApi.endpoints.createClaimDigitalExit;
  if (isApiPublicV4) method = coreApi.endpoints.createClaimDigitalExitV4;
  if (isApiPublicV5) method = coreApi.endpoints.createClaimDigitalExitV5;
  const claimId = getClaimId(state);
  if (!claimId) return;
  let errorCode = getErrorCode(state);
  const { INTERNAL_SERVER_ERROR, TECHNICAL_ERROR } = ERROR_CODES;
  if (errorCode === INTERNAL_SERVER_ERROR) errorCode = TECHNICAL_ERROR;
  const exitReason = customCode || errorCode;
  const callback = includes(exitReason, digitalExitReasonsToBeCalledBack);

  piwik.trackEvent({ ...eventTags.digital_exit, event_label: exitReason });

  const data = {
    callback,
    exitReason,
  };
  return requestCoreApi(dispatch, { method, data, claimId, auth }).then(() =>
    dispatch({ type: CLAIM_DIGITAL_EXIT_CREATED }),
  );
};

export const createMissionDigitalExit = customCode => () => (dispatch, getState) => {
  const state = getState();
  const missionId = Number(getMissionId(state));

  createMissionDigitalExitLogic(missionId, dispatch, state, customCode);
};

export const createRentalMissionDigitalExit = customCode => () => (dispatch, getState) => {
  const state = getState();
  const rentalMissionId = getCoreRentalMissionId(state);

  return createMissionDigitalExitLogic(rentalMissionId, dispatch, state, customCode);
};

const createMissionDigitalExitLogic = (missionId, dispatch, state, customCode) => {
  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);
  let method = coreApi.endpoints.createMissionDigitalExit;

  if (isApiPublicV4) {
    method = coreApi.endpoints.createMissionDigitalExitV4;
  }
  if (isApiPublicV5) {
    method = coreApi.endpoints.createMissionDigitalExitV5;
  }

  const claimId = getClaimId(state);

  if (!(claimId && missionId)) {
    return;
  }

  let errorCode = getErrorCode(state);
  const { INTERNAL_SERVER_ERROR, TECHNICAL_ERROR } = ERROR_CODES;

  if (errorCode === INTERNAL_SERVER_ERROR) {
    errorCode = TECHNICAL_ERROR;
  }

  const exitReason = customCode || errorCode;
  const callback = includes(exitReason, digitalExitReasonsToBeCalledBack);

  piwik.trackEvent({ ...eventTags.digital_exit, event_label: exitReason });

  const data = {
    callback,
    exitReason,
  };

  return requestCoreApi(dispatch, {
    method,
    data,
    claimId,
    missionId,
    auth,
  });
};

export const putClaimEvent = () => (dispatch, getState) => {
  const state = getState();
  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);
  let method = coreApi.endpoints.putClaimEvent;
  if (isApiPublicV4) method = coreApi.endpoints.putClaimEventV4;
  if (isApiPublicV5) method = coreApi.endpoints.putClaimEventV5;
  const data = {
    ...getEvent(state),
    ...getEventDetails(state),
  };
  const claimId = getClaimId(state);
  dispatch({ type: PUT_CLAIM });
  return requestCoreApi(dispatch, { method, claimId, data, auth }).then(() =>
    dispatch({ type: CLAIM_PUTTED, claim: { event: data } }),
  );
};

export const updateMissionType = () => (dispatch, getState) => {
  const state = getState();
  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };
  const isApiPublicV4 = getIsPublicApiV4(state);
  const isApiPublicV5 = getIsPublicApiV5(state);
  let method = coreApi.endpoints.updateMissionType;
  if (isApiPublicV4) method = coreApi.endpoints.updateMissionTypeV4;
  if (isApiPublicV5) method = coreApi.endpoints.updateMissionTypeV5;

  const claimId = getClaimId(state);
  const mission = getMission(state);
  const appointment = getAppointment(state);
  const data = isEmpty(appointment)
    ? { missionType: MISSION_TYPE.IMMEDIATE, pickupTime: undefined }
    : { missionType: MISSION_TYPE.APPOINTMENT, pickupTime: { ...appointment } };
  return requestCoreApi(dispatch, {
    method,
    claimId,
    missionId: mission.id,
    data,
    auth,
  }).then(() => dispatch({ type: MISSION_PUTTED, mission: { ...mission, ...data } }));
};

export const requestOTP = () => (dispatch, getState) => {
  const state = getState();
  const isApiPublicV5 = getIsPublicApiV5(state);
  let method = coreApi.endpoints.requestOTP;
  if (isApiPublicV5) method = coreApi.endpoints.requestOTPV5;
  const auth = { accessToken: getAccessToken(state) };
  const data = {
    phone: getPhoneNumber(state),
    plateNumber: getPlateNumber(state),
  };
  return requestCaptchaToken(state, CAPTCHA_ACTIONS.REQUEST_OTP)
    .then(captchaToken => requestCoreApi(dispatch, { method, auth, data, captchaToken }))
    .then(() => dispatch({ type: OTP_REQUESTED }));
};

export const generateJWT = () => (dispatch, getState) => {
  const state = getState();
  const isApiPublicV5 = getIsPublicApiV5(state);
  let method = coreApi.endpoints.getJWT;
  if (isApiPublicV5) method = coreApi.endpoints.getJWTV5;

  const data = {
    phone: getPhoneNumber(state),
    otp: getOtp(state),
  };
  const auth = { accessToken: getAccessToken(state) };
  return requestCoreApi(dispatch, { method, auth, data }).then(({ token: jwt }) => {
    const session = getSession(state);
    webStorage.setJwt(session, jwt);
    return dispatch({ type: JWT_CREATED, payload: { jwt } });
  });
};

export const getProductConfiguration = () => (dispatch, getState) => {
  const state = getState();
  const claim = getClaim(state);
  const productCode = claim?.subscription?.product?.productCode;
  const isApiPublicV5 = getIsPublicApiV5(state);
  let method = coreApi.endpoints.getProductConfigurationV4;
  if (isApiPublicV5) method = coreApi.endpoints.getProductConfigurationV5;
  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };
  return requestCoreApi(dispatch, {
    productCode,
    method,
    auth,
  }).then(configuration => dispatch({ type: PRODUCT_CONFIGURATION_LOADED, configuration }));
};

export const getClaimHistoryDispatch = () => (dispatch, getState) => {
  const state = getState();
  const isApiPublicV5 = getIsPublicApiV5(state);

  const auth = {
    accessToken: getAccessToken(state),
    jwt: getJwt(state),
  };

  const claimId = getClaimId(state);
  let method = coreApi.endpoints.getClaimHistoryV4;
  if (isApiPublicV5) method = coreApi.endpoints.getClaimHistoryV5;

  return requestCoreApi(dispatch, { claimId, method, auth }).then(history =>
    dispatch({ type: CLAIM_HISTORY_DISPATCH, history }),
  );
};

export const checkCscEligibility = () => (dispatch, getState) => {
  const state = getState();
  const jwt = getJwt(state);
  const auth = { accessToken: getAccessToken(state), jwt };

  const claimId = getClaimId(state);
  const benefitCode = getBenefit(state) || BENEFITS.REPLACEMENT_CAR;
  const businessPartnerCode = getBusinessPartnerCode(state);
  const eventCode = getEventCode(state);
  const requestedDatetime = new Date();

  let method = coreApi.endpoints.checkCscEligibilityV4;
  if (getIsPublicApiV5(state)) {
    method = coreApi.endpoints.checkCscEligibilityV5;
  }

  return requestCoreApi(dispatch, {
    method,
    claimId,
    data: { benefitCode, businessPartnerCode, eventCode, requestedDatetime },
    auth,
  }).then(response => !response.errorCode && dispatch({ type: CSC_ELIGIBILE, response }));
};

export const updateImmobilizationGarage = () => (dispatch, getState) => {
  const state = getState();
  const jwt = getJwt(state);
  const auth = { accessToken: getAccessToken(state), jwt };

  const claimId = getClaimId(state);

  const garageName = getRentalVehicleOtherGarageName(state);
  const garageEmail = getRentalVehicleOtherGarageEmail(state);
  const garageAddress = getRentalVehicleOtherLocationAddressObject(state);
  const garagePhone = getRentalVehicleOtherGaragePhone(state);

  const { position: location, ...rest } = garageAddress;
  const locationDetails = { location, ...rest };

  const data = { name: garageName, email: garageEmail, locationDetails, phoneNumber: garagePhone };

  let method = coreApi.endpoints.updateImmobilizationGarageV4;
  if (getIsPublicApiV5(state)) {
    method = coreApi.endpoints.updateImmobilizationGarageV5;
  }

  return requestCoreApi(dispatch, {
    method,
    claimId,
    data,
    auth,
  }).then(response => !response.errorCode && dispatch({ type: IMMOBILISATION_GARAGE_UPDATED }));
};

export const getRentalCurrentLimits = () => (dispatch, getState) => {
  const state = getState();
  const jwt = getJwt(state);
  const auth = { accessToken: getAccessToken(state), jwt };

  const claimId = getClaimId(state);
  const missionId = getCoreRentalMissionId(state);

  let method = coreApi.endpoints.rentalCurrentLimitsV4;
  if (getIsPublicApiV5(state)) {
    method = coreApi.endpoints.rentalCurrentLimitsV5;
  }

  return requestCoreApi(dispatch, {
    method,
    claimId,
    missionId,
    auth,
  }).then(response => !response.errorCode && dispatch({ type: RENTAL_CURRENT_LIMITS, response }));
};

export const updateRentalExtensionInfo = info => () => (dispatch, getState) => {
  const state = getState();
  const jwt = getJwt(state);
  const auth = { accessToken: getAccessToken(state), jwt };

  const claimId = getClaimId(state);
  const missionId = getCoreRentalMissionId(state);

  const method = coreApi.endpoints.rentalExtensionInfo;
  const version = getIsPublicApiV5(state) ? PUBLIC_API_VERSION.V5 : PUBLIC_API_VERSION.V4;

  const data = { isEligibleForExtension: info?.isEligibleForExtension };

  return requestCoreApi(dispatch, {
    method,
    version,
    auth,
    claimId,
    missionId,
    data,
  });
};

export const storeSelectedBenefit = ({ payload }) => ({ type: SELECT_BENEFIT, payload });

export const clearJWT = () => ({ type: CLEAR_JWT });

export const startRentalExtension = () => dispatch => {
  dispatch(getRentalCurrentLimits()).then(() => {
    dispatch({
      type: RENTAL_EXTENSION_WORKFLOW,
    });
  });
};

const actions = {
  createClaimV1,
  getClaim,
  loadClaimByPlateNumber,
  loadClaimById,
  authenticate,
  refreshToken,
  resetRetryCounter,
  retryNotFoundModal,
  retryExpiredModal,
  retryClaimExist,
  loadCarDestinationsInfo,
  searchMissionV1,
  cancelMission,
  automaticCancelMission,
  startMissionMonitoring,
  endMissionMonitoring,
  getBrandSuggestions,
  getModelSuggestions,
  clearMissionMonitoring,
  searchMissionByPreviousIdV1,
  continueProcess,
  getHighwaySections,
  getVehicleInfo,
  putClaimVehicleLoss,
  getInsuranceCompanies,
  createClaimDigitalExit,
  createMissionDigitalExit,
  createRentalMissionDigitalExit,
  putClaimLocation,
  putClaimEvent,
  createMissionV1,
  putVehicleDestination,
  getSpecificCarDestinations,
  dispatchMissionV1,
  dispatchRentalMissionV1,
  updateMissionV1,
  updateMissionType,
  digitalInteractionsV1,
  clearVehicleDestinationValues,
  requestOTP,
  generateJWT,
  npsEligibilityClaim,
  putNpsEligibilityClaim,
  clearJWT,
  getProductConfiguration,
  getClaimHistoryDispatch,
  createRentalMission,
  updateRentalMission,
  extendRentalMission,
  checkCscEligibility,
  storeSelectedBenefit,
  updateImmobilizationGarage,
  getRentalCurrentLimits,
  startRentalExtension,
  updateRentalExtensionInfo,
};

export default { reducer, actions };
